import React from 'react';
import {Stack, Typography} from '@mui/material';

import {AppointmentBookingEnum, FinishWithoutCompleteFlowEnum} from 'main/analytics/gaTagsEnum';
import {AlertModal, CTAButtonFixed} from 'presentation/components';
import {BookingEmptyState} from 'presentation/modules/booking/shared/ui/components/BookingEmptyState';
import {BookingErrorState} from 'presentation/modules/booking/shared/ui/components/BookingErrorState';
import {BookingLoadingState} from 'presentation/modules/booking/shared/ui/components/BookingLoadingState';
import {BookingNotFoundVariantEnum} from 'presentation/modules/booking/shared/ui/components/BookingNotFoundDialog/types';
import {BookingNotFoundListCard} from 'presentation/modules/booking/shared/ui/components/BookingNotFoundListCard';
import {DistrictList} from 'presentation/modules/booking/shared/ui/components/DistrictList';
import {translations} from 'presentation/translations-new';
import {DistrictsProps} from './types';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {useHealthDistrictSelection} from './hooks/useHealthDistrictSelection';
import {SpecialtyNotAvailableOnlineCard} from 'presentation/modules/booking/shared/ui/components/SpecialtyNotAvailableOnlineCard';
import {RequestSchedulePermissionCard} from 'presentation/modules/booking/shared/ui/components/RequestSchedulePermissionCard';
import {HealthDistrictNotFoundState} from './components/HealthDistrictNotFoundState';

const {
	loadingError,
	loadingState,
	emptySearchState,
	notFoundModal: {cardListText},
	emptyModal: {part1, part2},
} = translations['pt-br'].privateFlow.bookingFlow.districtFlow;
const {goForward, notice, understood} = translations['pt-br'].commons;

export function HealthDistrictSelection({handleNextStep}: DistrictsProps) {
	const {
		showEmptyState,
		isLoading,
		districtListHasError,
		districtListHasUnprocessableError,
		refetch,
		filteredDistrictList,
		selectedDistricts,
		showEmptyDistrictsModal,
		setShowEmptyDistrictsModal,
		handleSelectCard,
		handleGoForward,
		specialtyNotAvailableOnline,
		showNotFoundState,
		allowsRequestScheduleRegistration,
		healthRequestSchedulePermissionState,
	} = useHealthDistrictSelection({handleNextStep});

	if (districtListHasError && !districtListHasUnprocessableError) {
		return (
			<BookingErrorState
				showHeader={false}
				errorTitle={loadingError}
				handleRefresh={refetch}
			/>
		);
	}

	if (isLoading) {
		return <BookingLoadingState showHeader={false} loadingText={loadingState} />;
	}

	if (specialtyNotAvailableOnline) {
		return <SpecialtyNotAvailableOnlineCard />;
	}

	if (allowsRequestScheduleRegistration) {
		return (
			<RequestSchedulePermissionCard
				healthRequestSchedulePermission={healthRequestSchedulePermissionState}
			/>
		);
	}

	if (showNotFoundState) {
		return <HealthDistrictNotFoundState handleNextStep={handleNextStep} />;
	}

	if (showEmptyState) {
		return (
			<BookingEmptyState
				description={emptySearchState.emptyDistrictAll}
				flowType={FlowEnum.HEALTH}
				dataTagId={FinishWithoutCompleteFlowEnum.SEM_BAIRRO_ENCONTRADO}
			/>
		);
	}

	return (
		<Stack gap={2}>
			<DistrictList
				list={filteredDistrictList}
				handleSelectCard={handleSelectCard}
				selectedDistricts={selectedDistricts}
			/>
			<Stack mt={2} id="go-forward-button">
				<BookingNotFoundListCard
					flowType={FlowEnum.HEALTH}
					customOrientationText={cardListText}
					variant={BookingNotFoundVariantEnum.DISTRICT}
					openModalDataTagId={AppointmentBookingEnum.CLICK_NAO_ENCONTREI_BAIRRO_DESEJADO}
					modalHeaderModalDataTagId={
						AppointmentBookingEnum.VISUALIZOU_MODAL_NAO_ENCONTREI_BAIRRO_DESEJADO
					}
					modalFirstActionDataTagId={
						AppointmentBookingEnum.CLICK_CONSULTAR_REDE_ATENDIMENTO
					}
					modalCloseModalDataTagId={
						AppointmentBookingEnum.CLICK_FECHAR_MODAL_NAO_ENCONTREI_BAIRRO_DESEJADO
					}
					modalSecondActionDataTagId={
						AppointmentBookingEnum.CLICK_FALAR_COM_ATENDIMENTO_ONLINE
					}>
					<CTAButtonFixed.Root>
						<CTAButtonFixed.Action fullWidth onClick={handleGoForward} size="large">
							{goForward}
						</CTAButtonFixed.Action>
					</CTAButtonFixed.Root>
				</BookingNotFoundListCard>
			</Stack>

			{showEmptyDistrictsModal ? (
				<AlertModal
					open={showEmptyDistrictsModal}
					headerTitle={notice}
					variant="info"
					onClose={() => setShowEmptyDistrictsModal(false)}
					buttonLabel={understood}
					slotProps={{
						paper: {
							style: {
								maxWidth: 420,
								width: '100%',
							},
						},
					}}
					subDescription={
						<Typography variant="h6" fontSize={18} textAlign="center" fontWeight={600}>
							{part1} <br /> {part2}
						</Typography>
					}
				/>
			) : null}
		</Stack>
	);
}
